import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { api } from './api';
import { useWSEvent } from './hooks';

// Moderator server mutes, as { serverId → userIds }. Its own tiny store (not a
// slice of store.ts) so a mute flip re-renders only the badges reading it.
// Seeded from GET /voice/active and kept live by `voice.server_mute`, which is
// server-scoped — every member of that server receives it.
//
// Display only. Enforcement lives in LiveKit (the token + updateParticipant
// revoke the mic source), so a stale entry here can't let anyone talk.

interface VoiceMutesState {
  muted: Record<string, string[]>;
  replace: (m: Record<string, string[]>) => void;
  set: (serverId: string, userId: string, muted: boolean) => void;
}

const useVoiceMutesStore = create<VoiceMutesState>((set) => ({
  muted: {},
  replace: (muted) => set({ muted }),
  set: (serverId, userId, isMuted) =>
    set((s) => {
      const cur = s.muted[serverId] ?? [];
      if (isMuted === cur.includes(userId)) return s;
      const next = isMuted ? [...cur, userId] : cur.filter((u) => u !== userId);
      return { muted: { ...s.muted, [serverId]: next } };
    }),
}));

/** True when a moderator has server-muted `userId` in `serverId`. */
export function useIsServerMuted(serverId: string | null | undefined, userId: string): boolean {
  return useVoiceMutesStore((s) => (serverId ? (s.muted[serverId]?.includes(userId) ?? false) : false));
}

const NONE: string[] = [];
/** All server-muted user ids in a server — for lists, where a per-row hook
 *  isn't possible. Stable reference while unchanged. */
export function useServerMutedIds(serverId: string | null | undefined): string[] {
  return useVoiceMutesStore((s) => (serverId ? (s.muted[serverId] ?? NONE) : NONE));
}

/** Mount once (AppShell): seeds the map and keeps it live. */
export function useVoiceMutesSync(): void {
  const refetch = useCallback(() => {
    api
      .activeVoiceUsers()
      .then((r) => useVoiceMutesStore.getState().replace(r.serverMuted ?? {}))
      .catch(() => {});
  }, []);
  useEffect(() => refetch(), [refetch]);
  // A WS reconnect may have missed events.
  useWSEvent('hello', () => refetch());
  useWSEvent('voice.server_mute', (e) => useVoiceMutesStore.getState().set(e.serverId, e.userId, e.muted));
}
